import type { components } from "@countdown/api-client";
import { Building2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { can, tenantSettingLines } from "@/lib/labels";

type CellPolicy = components["schemas"]["CellPolicyView"];

/**
 * The tenant next to the bell, for support accounts only: its name, what its settings say and
 * where they are changed. Changing happens on the platform host, never here, so the menu only
 * shows and links; the session leaves `platformUrl` empty for everybody else. The support role
 * opens no element, so the link to the access protocol appears only with `audit`.
 */
export function TenantMenu({
  name,
  policy,
  platformUrl,
  granted,
}: {
  readonly name: string;
  readonly policy: CellPolicy;
  readonly platformUrl: string;
  readonly granted: readonly string[];
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={`Mandant ${name}`}>
          <Building2 aria-hidden="true" />
          <span className="max-w-[12rem] truncate">{name}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <p className="border-b px-4 py-2 text-sm font-bold">{name}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 px-4 py-3 text-sm">
          {tenantSettingLines(policy).map(([label, value]) => (
            <div className="contents" key={label}>
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="break-all">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-wrap gap-4 border-t px-4 py-2 text-sm">
          <a
            className="underline underline-offset-4"
            href={platformUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Einstellungen bearbeiten
          </a>
          {can(granted, "audit") ? (
            <Link
              className="underline underline-offset-4"
              to="/protokoll#zugriffe"
              onClick={() => {
                setOpen(false);
              }}
            >
              Zugriffe
            </Link>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
